import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import EpButton from '../EpButton';
import EpFormSelect, { changeSelectValue } from '../EpFormSelect';
import { storeLastFiltersApplied } from '../../redux/actions/filterAction';
import indicatorsService from '../../services/indicatorsService';
import useTranslation from '../../hooks/useTranslation';
import './styles.scss';

const EpBrowsableFilter = ({
  labelText, selectOptions, inputId, selectIsSearchable,
  placeholderText, classError, onBlurFunc, inputName, horizontalAlign,
  selectDefaultValue, lastFiltersApplied,
  setConsolidatedTotalizerIndicatorsDataFunc, setConsolidatedIndicatorsDataFunc,
  setEvolutiveIndicatorsDataFunc, setIsLoadingPageFunc, browsableFilterSelectRef,
}) => {
  // Tags do Globalization
  const Previous = useTranslation('Previous');
  const Next = useTranslation('Next');

  const dispatch = useDispatch();

  const totalOptions = selectOptions ? selectOptions.length : 0;
  const [hasPreviousControls, setHasPreviousControls] = useState(false);
  const [hasNextControls, setHasNextControls] = useState(false);
  const [selectValue, setSelectValue] = useState(selectDefaultValue);
  const [currentFilterIndex, setCurrentFilterIndex] = useState(
    selectOptions ? selectOptions.findIndex(option => option === selectDefaultValue) : null,
  );

  // Atualiza filtros aplicados na store
  const updateFiltersAppliedStorage = (selectedValue) => {
    const newFiltersAppliedObj = {
      ...lastFiltersApplied,
      [inputName]: selectedValue,
    };

    dispatch(storeLastFiltersApplied(newFiltersAppliedObj));
    return newFiltersAppliedObj;
  };

  // Valores iniciais dos campos do filtro
  const browsableFilterInitialValues = {
    [inputName]: selectDefaultValue,
  };

  // Método para tratar aplicação do filtro
  const handleBrowsableFilterSubmit = async (values, setValuesFunc) => {
    setIsLoadingPageFunc(true);
    setCurrentFilterIndex(selectOptions.findIndex(
      option => option.value === values[inputName].value,
    ));

    const newFiltersAppliedObj = updateFiltersAppliedStorage(values[inputName].value);

    let queryStrings = {
      selectedPatientGroup: newFiltersAppliedObj.patientGroupItemCode,
      selectedIndicator: newFiltersAppliedObj.indicator,
    };

    if (lastFiltersApplied.reportType === 0) {
      queryStrings = {
        selectedYear: newFiltersAppliedObj.year,
        selectedPatientGroup: newFiltersAppliedObj.patientGroupItemCode,
      };

      const consolidatedIndicatorsTotalizersResponseJSON = await indicatorsService()
        .getConsolidatedTotalizers(queryStrings);
      const consolidatedIndicatorsTotalizersList = consolidatedIndicatorsTotalizersResponseJSON
      && consolidatedIndicatorsTotalizersResponseJSON.data
        ? consolidatedIndicatorsTotalizersResponseJSON.data.response : null;

      const consolidatedIndicatorsResponseJSON = await indicatorsService()
        .getConsolidatedIndicators(queryStrings);
      const consolidatedIndicatorsList = consolidatedIndicatorsResponseJSON
      && consolidatedIndicatorsResponseJSON.data
        ? consolidatedIndicatorsResponseJSON.data.response : null;

      setConsolidatedTotalizerIndicatorsDataFunc(consolidatedIndicatorsTotalizersList || null);
      setConsolidatedIndicatorsDataFunc(consolidatedIndicatorsList || null);
    } else {
      const evolutiveIndicatorsResponseJSON = await indicatorsService()
        .getEvolutiveIndicators(queryStrings);

      const evolutiveIndicatorsList = evolutiveIndicatorsResponseJSON
      && evolutiveIndicatorsResponseJSON.data
        ? evolutiveIndicatorsResponseJSON.data.response : null;
      setEvolutiveIndicatorsDataFunc(evolutiveIndicatorsList || null);
    }

    setIsLoadingPageFunc(false);
    setValuesFunc(values[inputName]);
  };

  // Métodos para trocar a opção selecionada no filtro através dos controles de anterior e próximo
  const handleGoToFilterOption = (
    indicatorIndex, handleChangeFunc, setFieldValueFunc, // setValuesFunc,
  ) => {
    const newIndicatorIndex = Math.max(0, Math.min(indicatorIndex, totalOptions - 1));
    const indicatorOptionObj = {
      [inputName]: selectOptions[newIndicatorIndex],
    };

    handleChangeFunc(inputName);
    setFieldValueFunc(inputName, indicatorOptionObj[inputName]);
    setSelectValue(indicatorOptionObj[inputName]);
    setCurrentFilterIndex(newIndicatorIndex);
    changeSelectValue(browsableFilterSelectRef, selectOptions[newIndicatorIndex]);
  };

  const handleMoveToPreviousFilterOption = (handleChangeFunc, setFieldValueFunc, setValuesFunc) => {
    handleGoToFilterOption(
      inputName === 'year' ? currentFilterIndex + 1 : currentFilterIndex - 1, handleChangeFunc, setFieldValueFunc, setValuesFunc,
    );
  };

  const handleMoveToNextFilterOption = (handleChangeFunc, setFieldValueFunc, setValuesFunc) => {
    handleGoToFilterOption(
      inputName === 'year' ? currentFilterIndex - 1 : currentFilterIndex + 1, handleChangeFunc, setFieldValueFunc, setValuesFunc,
    );
  };

  // Método para habilitar ou não controles de anterior e próximo
  const handleControlsVisibility = () => {
    const lastOptionsElementIndex = selectOptions ? totalOptions - 1 : 0;

    setHasPreviousControls(true);
    setHasNextControls(true);

    if (selectOptions === undefined || selectOptions === null || totalOptions <= 0) {
      setHasPreviousControls(false);
      setHasNextControls(false);
    }

    if (selectOptions === null || selectOptions === undefined || currentFilterIndex === 0) {
      if (inputName === 'year') {
        setHasNextControls(false);
      } else {
        setHasPreviousControls(false);
      }
    }

    if (selectOptions === null || selectOptions === undefined
      || currentFilterIndex === lastOptionsElementIndex) {
      if (inputName === 'year') {
        setHasPreviousControls(false);
      } else {
        setHasNextControls(false);
      }
    }
  };

  useEffect(() => {
    handleControlsVisibility();
  }, [currentFilterIndex]);

  return (
    <div className={`browsable-filter browsable-filter--${horizontalAlign}`}>
      <Formik
        initialValues={browsableFilterInitialValues}
        onSubmit={(values, actions) => handleBrowsableFilterSubmit(values, actions.setValues)}
      >
        {({
          handleSubmit, handleChange, setFieldValue, setValues,
        }) => (
          <Form className="browsable-filter__form">
            <EpButton
              actionType="button"
              buttonText={`« ${Previous}`}
              buttonType="link"
              customClass="browsable-filter__control browsable-filter__control--previous browsable-filter__control--first"
              onClickFunc={() => {
                handleMoveToPreviousFilterOption(
                  handleChange, setFieldValue, setValues,
                );
              }}
              disabled={!hasPreviousControls}
              buttonSize="sm"
            />

            <EpFormSelect
              minPortalWidth={220}
              customRef={browsableFilterSelectRef}
              classError={classError}
              labelText={labelText}
              isSearchable={selectIsSearchable}
              onBlurFunc={onBlurFunc}
              inputName={inputName}
              inputId={inputId}
              placeholderText={placeholderText}
              selectValue={{ label: selectValue ? selectValue.label : '', value: selectValue ? selectValue.value : null }}
              selectDefaultValue={selectDefaultValue}
              onChangeFunc={(option) => {
                setSelectValue(option);
                handleChange(inputName);
                setFieldValue(inputName, option);
                handleSubmit();
              }}
              selectOptions={selectOptions}
            />

            <div className="browsable-filter__form-button--responsive">
              <EpButton
                actionType="button"
                buttonText={`« ${Previous}`}
                buttonType="link"
                customClass="browsable-filter__control browsable-filter__control--previous browsable-filter__control--second"
                onClickFunc={() => {
                  handleMoveToPreviousFilterOption(
                    handleChange, setFieldValue, setValues,
                  );
                }}
                disabled={!hasPreviousControls}
                buttonSize="sm"
              />

              <EpButton
                actionType="button"
                buttonText={`${Next} »`}
                buttonType="link"
                customClass="browsable-filter__control browsable-filter__control--next"
                onClickFunc={() => handleMoveToNextFilterOption(
                  handleChange, setFieldValue, setValues,
                )}
                disabled={!hasNextControls}
                buttonSize="sm"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EpBrowsableFilter;

EpBrowsableFilter.defaultProps = {
  placeholderText: '',
  selectOptions: null,
  selectDefaultValue: null,
  selectIsSearchable: true,
  classError: '',
  horizontalAlign: 'space-between',
  browsableFilterSelectRef: null,
  onBlurFunc: null,
};

EpBrowsableFilter.propTypes = {
  placeholderText: PropTypes.string,
  selectOptions: PropTypes.arrayOf(PropTypes.object),
  labelText: PropTypes.string.isRequired,
  inputName: PropTypes.string.isRequired,
  selectDefaultValue: PropTypes.objectOf(PropTypes.any),
  inputId: PropTypes.string.isRequired,
  selectIsSearchable: PropTypes.bool,
  classError: PropTypes.string,
  onBlurFunc: PropTypes.func,
  horizontalAlign: PropTypes.oneOf(['space-between', 'center']),
  lastFiltersApplied: PropTypes.objectOf(PropTypes.any).isRequired,
  setConsolidatedTotalizerIndicatorsDataFunc: PropTypes.func.isRequired,
  setConsolidatedIndicatorsDataFunc: PropTypes.func.isRequired,
  setEvolutiveIndicatorsDataFunc: PropTypes.func.isRequired,
  setIsLoadingPageFunc: PropTypes.func.isRequired,
  browsableFilterSelectRef: PropTypes.objectOf(PropTypes.any),
};
